import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Helper functions for base64 encoding/decoding
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

// Generate random challenge
function generateChallenge(): ArrayBuffer {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return array.buffer as ArrayBuffer;
}

interface StoredCredential {
  credentialId: string;
  email: string;
  publicKey: string;
}

// Detect if running in iframe (WebAuthn doesn't work in cross-origin iframes)
const isInIframe = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    return true; // If we can't access window.top, we're in a cross-origin iframe
  }
};

export function useWebAuthn() {
  const [isSupported, setIsSupported] = useState(() => {
    // WebAuthn is not supported in cross-origin iframes
    if (isInIframe()) return false;
    return !!(window.PublicKeyCredential && navigator.credentials);
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isIframeBlocked] = useState(() => isInIframe());

  // Check if biometric is available on device
  const checkBiometricAvailability = useCallback(async (): Promise<boolean> => {
    if (!isSupported) return false;
    
    try {
      const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      return available;
    } catch {
      return false;
    }
  }, [isSupported]);

  // Check if user has registered biometric
  const hasRegisteredBiometric = useCallback((): boolean => {
    const stored = localStorage.getItem('foco_webauthn_credential');
    return !!stored;
  }, []);

  // Get stored credential
  const getStoredCredential = useCallback((): StoredCredential | null => {
    const stored = localStorage.getItem('foco_webauthn_credential');
    if (!stored) return null;
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }, []);

  // Register biometric for current user
  const registerBiometric = useCallback(async (email: string): Promise<boolean> => {
    if (!isSupported) {
      setError('WebAuthn no está soportado en este dispositivo');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      const challenge = generateChallenge();
      
      const publicKeyCredentialCreationOptions: PublicKeyCredentialCreationOptions = {
        challenge,
        rp: {
          name: 'FOCO Dashboard Pro',
          id: window.location.hostname,
        },
        user: {
          id: new TextEncoder().encode(email),
          name: email,
          displayName: email.split('@')[0],
        },
        pubKeyCredParams: [
          { alg: -7, type: 'public-key' },   // ES256
          { alg: -257, type: 'public-key' }, // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform', // Use device biometric
          userVerification: 'required',
          residentKey: 'preferred',
        },
        timeout: 60000,
        attestation: 'none',
      };

      const credential = await navigator.credentials.create({
        publicKey: publicKeyCredentialCreationOptions,
      }) as PublicKeyCredential;

      if (!credential) {
        throw new Error('No se pudo crear la credencial');
      }

      const response = credential.response as AuthenticatorAttestationResponse;
      
      // Store credential locally
      const storedCredential: StoredCredential = {
        credentialId: arrayBufferToBase64(credential.rawId),
        email,
        publicKey: arrayBufferToBase64(response.getPublicKey() || new ArrayBuffer(0)),
      };
      
      localStorage.setItem('foco_webauthn_credential', JSON.stringify(storedCredential));
      
      setIsLoading(false);
      return true;
    } catch (err: any) {
      console.error('Biometric registration error:', err);
      setError(err.message || 'Error al registrar biometría');
      setIsLoading(false);
      return false;
    }
  }, [isSupported]);

  // Authenticate with biometric
  const authenticateWithBiometric = useCallback(async (): Promise<{ email: string } | null> => {
    if (!isSupported) {
      setError('WebAuthn no está soportado en este dispositivo');
      return null;
    }

    const storedCredential = getStoredCredential();
    if (!storedCredential) {
      setError('No hay credencial biométrica registrada');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const challenge = generateChallenge();
      
      const publicKeyCredentialRequestOptions: PublicKeyCredentialRequestOptions = {
        challenge,
        allowCredentials: [{
          id: base64ToArrayBuffer(storedCredential.credentialId),
          type: 'public-key',
          transports: ['internal'],
        }],
        userVerification: 'required',
        timeout: 60000,
        rpId: window.location.hostname,
      };

      const assertion = await navigator.credentials.get({
        publicKey: publicKeyCredentialRequestOptions,
      }) as PublicKeyCredential;

      if (!assertion) {
        throw new Error('Autenticación biométrica fallida');
      }

      setIsLoading(false);
      return { email: storedCredential.email };
    } catch (err: any) {
      console.error('Biometric authentication error:', err);
      setError(err.message || 'Error en autenticación biométrica');
      setIsLoading(false);
      return null;
    }
  }, [isSupported, getStoredCredential]);

  // Remove stored biometric credential
  const removeBiometric = useCallback(() => {
    localStorage.removeItem('foco_webauthn_credential');
  }, []);

  return {
    isSupported,
    isLoading,
    error,
    isIframeBlocked,
    checkBiometricAvailability,
    hasRegisteredBiometric,
    getStoredCredential,
    registerBiometric,
    authenticateWithBiometric,
    removeBiometric,
  };
}
